import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { MessageCircle, Sparkles, Eye, X } from 'lucide-react';

const WHATSAPP_NUMBER = "919535834444";

const newArrivals = [
  {
    id: 1,
    name: 'Royal Burgundy Silk Saree',
    image: 'https://images.pexels.com/photos/5814563/pexels-photo-5814563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    isNew: true,
  },
  {
    id: 2,
    name: 'Designer Bridal Lehenga',
    image: 'https://images.unsplash.com/photo-1767955694884-d4bf352c23c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    isNew: true,
  },
  {
    id: 3,
    name: 'Embellished Evening Gown',
    image: 'https://images.unsplash.com/photo-1767790693645-2373e54d4f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    isNew: true,
  },
  {
    id: 4,
    name: 'Traditional Zardozi Work',
    image: 'https://images.unsplash.com/photo-1760613129745-418b15f91d56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    isNew: false,
  },
  {
    id: 5,
    name: 'Contemporary Crop Top Set',
    image: 'https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBjcm9wJTIwdG9wJTIwZXRobmljJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    isNew: true,
  },
  {
    id: 6,
    name: 'Festive Occasion Wear',
    image: 'https://images.pexels.com/photos/28943676/pexels-photo-28943676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    isNew: false,
  },
];

// Gold Sparkle Animation Component
const GoldSparkle = ({ isHovered }) => {
  if (!isHovered) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ 
            opacity: 0, 
            scale: 0,
            x: Math.random() * 100 + '%',
            y: Math.random() * 100 + '%',
          }}
          animate={{ 
            opacity: [0, 1, 0], 
            scale: [0, 1, 0],
          }}
          transition={{
            duration: 1,
            delay: i * 0.1,
            repeat: Infinity,
          }}
        >
          <Sparkles className="text-[#D4AF37]" size={16} />
        </motion.div>
      ))}
    </div>
  );
};

const ProductCard = ({ product, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const getWhatsAppLink = () => {
    const message = encodeURIComponent(`Hi! I'm interested in "${product.name}" from Devanand Vestment. Is this available?`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  return (
    <motion.div
      ref={ref}
      className="relative group"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`new-arrival-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-white rounded-2xl shadow-lg" style={{ boxShadow: '0 10px 40px -10px rgba(128, 0, 32, 0.25), 0 0 20px rgba(212, 175, 55, 0.15)' }}>
        {/* Product Image */}
        <div className="relative h-[350px] md:h-[420px] overflow-hidden rounded-t-2xl">
          <motion.img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.1 : 1 }}
            transition={{ duration: 0.6 }}
          />

          {/* Gold Sparkle Effect */}
          <GoldSparkle isHovered={isHovered} />

          {/* Dark Overlay on Hover */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* NEW Badge */}
          {product.isNew && (
            <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-black font-body text-xs tracking-wider font-medium rounded-full">
              NEW
            </div>
          )}

          {/* Action Buttons - Appear on Hover */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-body text-sm tracking-wider"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid={`new-arrival-whatsapp-${product.id}`}
            >
              <MessageCircle size={18} />
              <span>Enquire</span>
            </motion.a>
          </motion.div>
        </div>

        {/* Product Info */}
        <div className="p-5 bg-white text-center border-t border-[#E5E5E5] rounded-b-2xl">
          <h4 className="font-heading text-lg md:text-xl text-[#1A1A1A]">
            {product.name}
          </h4>
        </div>

        {/* Gold Border on Hover */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          animate={{
            boxShadow: isHovered 
              ? '0 0 40px rgba(212, 175, 55, 0.5), inset 0 0 0 3px rgba(212, 175, 55, 0.7)' 
              : '0 0 0 rgba(212, 175, 55, 0), inset 0 0 0 0 rgba(212, 175, 55, 0)',
          }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.div>
  );
};

export const NewArrivals = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="new-arrivals"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="new-arrivals-section"
    >
      <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-3 mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="text-[#D4AF37]" size={20} />
            <span className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase">
              Just Arrived
            </span>
            <Sparkles className="text-[#D4AF37]" size={20} />
          </motion.div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A]">
            New Arrivals
          </h2>
          <motion.div
            className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-6"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {newArrivals.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <motion.a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi! I'd like to see all your new arrivals. Can you share the catalog?")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#800020] text-white font-body text-sm tracking-widest uppercase hover:bg-[#600018] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="view-all-arrivals-btn"
          >
            <MessageCircle size={18} />
            <span>Request Full Catalog</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
